/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Empty, Input, Modal, Pagination, Table, Tag } from 'antd';
import { ChevronDown, ChevronRight, History, Search } from 'lucide-react';
import moment from 'moment';
import get from 'lodash/get';
import axiosConfig from '../redux/sparesDashboard/axiosConfig';
import { Button, Spinner } from '@crc-evans/ui-components';
import './SpareOrderHistoryModal.css';
import './DPR/DPRSettingsSparesTab.css';

const normalizeOrderRow = (record, index) => {
  if (!record || typeof record !== 'object') {
    return { id: `row-${index}` };
  }
  return {
    ...record,
    id: record.id ?? record.orderId ?? record.spareOrderId ?? `row-${index}`,
    createdAt: record.createdAt ?? record.orderedOn ?? record.orderDate ?? record.created_at,
    sapNumber: record.sapNumber ?? record.partSapNumber ?? record.sapNo ?? record.sap_number,
    description: record.description ?? record.partDescription ?? record.spareDescription,
    quantity: record.quantity ?? record.qty ?? record.orderQuantity,
    orderTypeLabel: record.orderTypeLabel ?? record.orderType ?? record.type,
    urgent: Boolean(record.urgent ?? record.isUrgent),
    orderedBy: record.orderedBy ?? record.orderedByName ?? record.userName ?? record.createdBy,
    orderedByEmail: record.orderedByEmail ?? record.userEmail ?? record.email,
    equipmentType: record.equipmentType ?? record.equipment ?? record.pihEquipmentType,
    equipmentSerialNumber:
      record.equipmentSerialNumber ??
      record.serialNumber ??
      record.assignedToSerialNumber ??
      record.equipmentSerialNo,
    sapKitNo: record.sapKitNo ?? record.sapSparesKitNumber ?? record.sapKitNumber,
    notes: record.notes ?? record.comment ?? record.remarks,
    projectName: record.projectName,
  };
};

const parseOrdersResponse = (res) => {
  const payload = get(res, 'data.data', get(res, 'data', {}));
  if (Array.isArray(payload)) {
    return { rows: payload, totalCount: payload.length };
  }
  const rows = payload.rows ?? payload.items ?? payload.orders ?? payload.data ?? [];
  const totalCount =
    Number(payload.totalCount ?? payload.total ?? payload.count ?? rows.length) || 0;
  return {
    rows: Array.isArray(rows) ? rows : [],
    totalCount,
  };
};

const SpareOrderHistoryModal = ({ open, projectId, onClose }) => {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
    setExpandedRowKeys([]);
  }, [debouncedSearch, projectId]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setDebouncedSearch('');
      setExpandedRowKeys([]);
    }
  }, [open]);

  const fetchOrders = useCallback(async () => {
    if (!open || !projectId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        projectId: String(projectId),
        pageNumber: String(page),
        pageSize: String(pageSize),
      });
      if (debouncedSearch) params.set('search', debouncedSearch);

      const res = await axiosConfig.get(`/project-spare-orders?${params.toString()}`);
      const { rows: rawRows, totalCount: total } = parseOrdersResponse(res);
      setRows(rawRows.map(normalizeOrderRow));
      setTotalCount(total);
    } catch (err) {
      console.error('Failed to load spare order history', err);
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [open, projectId, page, pageSize, debouncedSearch]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const toggleExpand = useCallback((rowId) => {
    setExpandedRowKeys((prev) =>
      prev.includes(rowId) ? prev.filter((k) => k !== rowId) : [...prev, rowId]
    );
  }, []);

  const renderDetailPanel = useCallback((record) => {
    const detailFields = [
      { label: 'Ordered by (email)', value: record.orderedByEmail },
      { label: 'Equipment type', value: record.equipmentType },
      { label: 'Serial number', value: record.equipmentSerialNumber },
      { label: 'SAP kit', value: record.sapKitNo },
      { label: 'Order type', value: record.orderTypeLabel },
      { label: 'Notes', value: record.notes },
      { label: 'Project', value: record.projectName },
    ].filter((f) => f.value != null && String(f.value).trim() !== '');

    if (detailFields.length === 0) {
      return (
        <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>
          No additional details for this order.
        </p>
      );
    }

    return (
      <dl className="spare-order-history-modal-detail">
        {detailFields.map((field) => (
          <div key={field.label}>
            <dt>{field.label}</dt>
            <dd>{field.value}</dd>
          </div>
        ))}
      </dl>
    );
  }, []);

  const columns = useMemo(
    () => [
      {
        title: '',
        key: 'expand',
        width: 40,
        fixed: 'left',
        render: (_, record) => {
          const expanded = expandedRowKeys.includes(record.id);
          return (
            <button
              type="button"
              className="spare-order-history-modal-expand-btn"
              onClick={() => toggleExpand(record.id)}
              aria-expanded={expanded}
              aria-label={expanded ? 'Collapse details' : 'Expand details'}
            >
              {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          );
        },
      },
      {
        title: 'ORDERED ON',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 150,
        render: (value) => (value ? moment(value).format('DD MMM YYYY HH:mm') : '—'),
      },
      {
        title: 'SAP NUMBER',
        dataIndex: 'sapNumber',
        key: 'sapNumber',
        width: 130,
        render: (text) => text || '—',
      },
      {
        title: 'DESCRIPTION',
        dataIndex: 'description',
        key: 'description',
        ellipsis: true,
        render: (text) => text || '—',
      },
      {
        title: 'QTY',
        dataIndex: 'quantity',
        key: 'quantity',
        width: 64,
        align: 'center',
        render: (value) => (value != null && value !== '' ? value : '—'),
      },
      {
        title: 'TYPE',
        dataIndex: 'orderTypeLabel',
        key: 'orderTypeLabel',
        width: 96,
        render: (text, record) =>
          record.urgent ? (
            <Tag className="spare-order-history-modal-type-tag spare-order-history-modal-type-tag--urgent">
              Urgent
            </Tag>
          ) : (
            <Tag className="spare-order-history-modal-type-tag spare-order-history-modal-type-tag--normal">
              {text || 'Normal'}
            </Tag>
          ),
      },
      {
        title: 'ORDERED BY',
        dataIndex: 'orderedBy',
        key: 'orderedBy',
        width: 150,
        ellipsis: true,
        render: (text, record) => text || record.orderedByEmail || '—',
      },
    ],
    [expandedRowKeys, toggleExpand]
  );

  const modalTitle = (
    <span className="spare-order-history-modal-title">
      <span className="spare-order-history-modal-title-icon" aria-hidden>
        <History size={18} />
      </span>
      Spare parts order history
    </span>
  );

  return (
    <Modal
      width={1000}
      className="spare-order-history-modal dpr-settings-spares-form-modal"
      wrapClassName="dpr-settings-spares-ant-modal-wrap spare-order-history-modal-wrap"
      title={modalTitle}
      centered
      visible={open}
      onCancel={onClose}
      maskClosable
      destroyOnClose
      footer={
        <div className="dpr-settings-spares-modal-footer">
          <Button label="Close" variant="tertiary" size="medium" onClick={onClose} />
        </div>
      }
    >
      <div className="spare-order-history-modal-toolbar">
        <Input
          allowClear
          className="spare-order-history-modal-search"
          prefix={<Search size={16} color="#94a3b8" aria-hidden />}
          placeholder="Search by SAP number, description, serial number, or ordered by"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="spare-order-history-modal-table-panel">
        <Table
          className="spare-order-history-table"
          rowKey="id"
          columns={columns}
          dataSource={rows}
          pagination={false}
          scroll={{ x: 900, y: 360 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  debouncedSearch
                    ? 'No orders match your search.'
                    : 'No spare part orders yet for this project.'
                }
              />
            ),
          }}
          expandable={{
            expandedRowKeys,
            expandIconColumnIndex: -1,
            expandedRowRender: renderDetailPanel,
            rowExpandable: () => true,
          }}
          loading={{
            spinning: loading,
            indicator: <Spinner variant="inline" size="large" color="#00C9A7" />,
          }}
        />
      </div>
      {totalCount > 0 && (
        <div className="spare-order-history-modal-pagination dpr-settings-spares-pagination">
          <Pagination
            className="heat-profile-pagination"
            current={page}
            pageSize={pageSize}
            total={totalCount}
            showSizeChanger
            pageSizeOptions={['10', '20', '50', '100']}
            onChange={(nextPage, nextSize) => {
              setPage(nextPage);
              if (nextSize != null) setPageSize(nextSize);
              setExpandedRowKeys([]);
            }}
            showTotal={(total, range) => `${range[0]} - ${range[1]} of ${total} orders`}
          />
        </div>
      )}
    </Modal>
  );
};

export default SpareOrderHistoryModal;
